import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/contexts/AuthContext';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useSourceContext } from '_/contexts/SourceContext';
import { useOrgAccess } from '_/hooks/usePermissions';
import { useUpdateSource } from '_/hooks/useSources';
import { Row, Col } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import React, { ReactElement, useEffect } from 'react';

import GeneralSettings from './components/GeneralSettings';

const SourceSettings = (): ReactElement => {
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { selectedCollection, selectedCollectionSlug } = useCollection();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const { setBreadcrumbs } = useTopHeader();
  const { selectedSource: source, selectedSourceSlug, isLoading: isFetchingSource } = useSourceContext();
  const { mutate: updateSourceMutation, isPending: isSaving } = useUpdateSource(selectedCollection?.id);

  const toggleAutomaticallyImportData = (e: CheckboxChangeEvent) => {
    if (!source) return;

    const autoImport = e.target.checked;
    updateSourceMutation({
      sourceId: source.id,
      updates: { autoImport: autoImport },
    });
  };

  // Set up breadcrumbs
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
    ];

    if (selectedCollection) {
      breadcrumbs.push({
        title: selectedCollection.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/home`,
      });
    }

    if (source) {
      breadcrumbs.push(
        {
          title: 'Sources',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources`,
        },
        {
          title: source.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/home`,
        },
        { title: 'Settings' },
      );
    }

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    setBreadcrumbs,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    selectedCollection,
    selectedCollectionSlug,
    source,
    selectedSourceSlug,
  ]);

  if (!source) {
    return <></>;
  }

  return (
    <Row>
      <Col xs={24} sm={12}>
        <GeneralSettings
          access={access}
          automaticallyImportData={!!source.autoImport}
          isFetchingSource={isFetchingSource}
          isSaving={isSaving}
          toggleAutomaticallyImportData={toggleAutomaticallyImportData}
        />
      </Col>
    </Row>
  );
};

export default SourceSettings;
